import { useEffect, useMemo, useState } from 'react';
import { Button, Typography, Card, Checkbox, MenuItem, Stack, TextField } from '@mui/material';
const sourceLabels = {
  user: 'משתמש',
  gps: 'GPS',
  ai: 'AI',
  unknown: 'לא מוכר',
};
const fieldLabels = {
  totalWeightMultiplier: 'מכפיל משקל מצטבר',
  totalWeightCap: 'תקרת משקל מצטבר',
  userBonus: 'בונוס לדיווח משתמש',
  userCap: 'תקרת משתמשים',
  gpsBonus: 'בונוס לדיווח GPS',
  gpsCap: 'תקרת GPS',
  aiBonus: 'בונוס לדיווח AI',
  aiCap: 'תקרת AI',
  disagreementFloor: 'רצפת חוסר הסכמה',
  disagreementPenalty: 'קנס סטיית תקן',
};
const realConfidenceFields = [
  'totalWeightMultiplier',
  'totalWeightCap',
  'userBonus',
  'userCap',
  'gpsBonus',
  'gpsCap',
  'disagreementFloor',
  'disagreementPenalty',
];
const aiConfidenceFields = ['aiBonus', 'aiCap'];
const weightForBucket = (value, buckets, fallback, maxKey) =>
  buckets.find((bucket) => Number(value) <= Number(bucket[maxKey]))?.weight ?? fallback;
const statusForScore = (score, statuses) =>
  statuses.find((status) => score <= status.maxScore) || statuses[statuses.length - 1];
const simulateReport = (report, settings) => {
  const source = settings.sourceWeights[report.source] ?? settings.sourceWeights.unknown;
  const location = report.locationVerified
    ? settings.locationWeight.verified
    : report.distanceMeters === ''
      ? settings.locationWeight.missing
      : weightForBucket(
          report.distanceMeters,
          settings.locationWeight.buckets,
          settings.locationWeight.fallback,
          'maxMeters',
        );
  const accuracy =
    report.accuracyMeters === ''
      ? settings.accuracyWeight.missing
      : weightForBucket(
          report.accuracyMeters,
          settings.accuracyWeight.buckets,
          settings.accuracyWeight.fallback,
          'maxMeters',
        );
  const freshness = weightForBucket(
    report.ageMinutes,
    settings.freshnessWeight.buckets,
    settings.freshnessWeight.fallback,
    'maxMinutes',
  );
  return {
    source,
    location,
    accuracy,
    freshness,
    total: source * location * accuracy * freshness,
  };
};
const AlgorithmSimulator = ({ settings }) => {
  const [reports, setReports] = useState([
    {
      id: 1,
      source: 'user',
      score: 7,
      distanceMeters: 30,
      accuracyMeters: 15,
      ageMinutes: 2,
      locationVerified: true,
    },
    {
      id: 2,
      source: 'gps',
      score: 6,
      distanceMeters: 100,
      accuracyMeters: 40,
      ageMinutes: 8,
      locationVerified: false,
    },
  ]);
  const results = useMemo(
    () => reports.map((report) => simulateReport(report, settings)),
    [reports, settings],
  );
  const weightedScore = (indexes) => {
    const weight = indexes.reduce((sum, index) => sum + results[index].total, 0);
    return weight
      ? indexes.reduce((sum, index) => sum + reports[index].score * results[index].total, 0) /
          weight
      : 5;
  };
  const realIndexes = reports
    .map((report, index) => ({
      report,
      index,
    }))
    .filter(({ report }) => report.source === 'user' || report.source === 'gps')
    .map(({ index }) => index);
  const aiIndexes = reports
    .map((report, index) => ({
      report,
      index,
    }))
    .filter(({ report }) => report.source === 'ai')
    .map(({ index }) => index);
  const realWeight = realIndexes.reduce((sum, index) => sum + results[index].total, 0);
  const realScore = weightedScore(realIndexes);
  const confidenceConfig = settings.confidence;
  const userCount = realIndexes.filter((index) => reports[index].source === 'user').length;
  const gpsCount = realIndexes.filter((index) => reports[index].source === 'gps').length;
  let confidence =
    Math.min(realWeight * confidenceConfig.totalWeightMultiplier, confidenceConfig.totalWeightCap) +
    Math.min(userCount * confidenceConfig.userBonus, confidenceConfig.userCap) +
    Math.min(gpsCount * confidenceConfig.gpsBonus, confidenceConfig.gpsCap);
  if (realIndexes.length >= 2 && realWeight > 0) {
    const variance =
      realIndexes.reduce(
        (sum, index) => sum + results[index].total * (reports[index].score - realScore) ** 2,
        0,
      ) / realWeight;
    confidence *= Math.max(
      confidenceConfig.disagreementFloor,
      1 - Math.sqrt(variance) * confidenceConfig.disagreementPenalty,
    );
  }
  confidence = Math.round(Math.min(confidence, 100));
  const aiScore = weightedScore(aiIndexes);
  const aiInfluence =
    realIndexes.length && aiIndexes.length
      ? Math.max(
          0,
          settings.aiBlend.maximumInfluence *
            (1 - confidence / settings.aiBlend.minimumRealConfidence),
        )
      : 0;
  const rawScore = realIndexes.length
    ? realScore * (1 - aiInfluence) + aiScore * aiInfluence
    : aiIndexes.length
      ? aiScore
      : 5;
  const score = Math.max(1, Math.min(10, Math.round(rawScore)));
  const status = statusForScore(score, settings.statuses);
  const update = (index, patch) =>
    setReports((current) =>
      current.map((report, reportIndex) =>
        reportIndex === index
          ? {
              ...report,
              ...patch,
            }
          : report,
      ),
    );
  return (
    <Stack
      sx={(theme) => ({
        display: 'grid',
        gap: '18px',
        padding: '20px',
        border: '1px solid var(--line)',
        borderRadius: '16px',
        background: '#fff',
        boxShadow: 'var(--shadow-sm)',
        [theme.breakpoints.down('sm')]: {
          padding: '14px',
        },
      })}
    >
      <Stack
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <Stack>
          <Typography>סימולטור דיווחים</Typography>
          <Typography
            sx={(theme) => ({
              color: 'var(--muted)',
              [theme.breakpoints.down('lg')]: {
                margin: '4px 0 0',
                fontSize: '12px',
                display: 'none',
              },
              margin: '2px 0 0',
              fontSize: '11px',
            })}
          >
            התוצאה מתעדכנת מיד לפי טיוטת ההגדרות.
          </Typography>
        </Stack>
        <Stack
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            '& strong': {
              fontSize: '30px',
            },
          }}
        >
          <Typography>ציון משוקלל</Typography>
          <Typography>
            ביטחון {confidence}% · השפעת AI {(aiInfluence * 100).toFixed(1)}%
          </Typography>
          <Typography>{score}</Typography>
          <Typography
            sx={String(`rule-status rule-status-${status.key}`)
              .split(/\s+/)
              .map(
                (name) =>
                  ({
                    'rule-status': {
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '36px',
                      padding: '5px 10px',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '800',
                    },
                    'rule-status-calm': {
                      background: '#dcfae6',
                      color: '#067647',
                    },
                    'rule-status-medium': {
                      background: '#fff6d6',
                      color: '#b54708',
                    },
                    'rule-status-busy': {
                      background: '#ffead5',
                      color: '#c4320a',
                    },
                    'rule-status-very_busy': {
                      background: '#fee4e2',
                      color: '#b42318',
                    },
                  })[name],
              )
              .filter(Boolean)}
          >
            {status.label}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        sx={{
          display: 'grid',
          gap: '8px',
          overflowX: 'auto',
        }}
      >
        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: '110px 70px 90px 90px 90px 105px minmax(260px, 1fr) 90px 75px',
            gap: '8px',
            alignItems: 'center',
            minWidth: '1100px',
            color: 'var(--muted)',
            fontSize: '12px',
            fontWeight: '800',
          }}
        >
          <Typography>מקור</Typography>
          <Typography>ציון</Typography>
          <Typography>מרחק</Typography>
          <Typography>דיוק</Typography>
          <Typography>גיל בדקות</Typography>
          <Typography>מיקום מאומת</Typography>
          <Typography>פירוק משקל</Typography>
          <Typography>משקל סופי</Typography>
          <Typography />
        </Stack>
        {reports.map((report, index) => {
          const result = results[index];
          return (
            <Stack
              key={report.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '110px 70px 90px 90px 90px 105px minmax(260px, 1fr) 90px 75px',
                gap: '8px',
                alignItems: 'center',
                minWidth: '1100px',
                padding: '8px',
                border: '1px solid var(--line)',
                borderRadius: '12px',
                background: '#f8fafc',
                '& select': {
                  minWidth: '0',
                },
              }}
            >
              <TextField
                value={report.source}
                onChange={(event) =>
                  update(index, {
                    source: event.target.value,
                  })
                }
                select
                fullWidth
              >
                {Object.entries(sourceLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                min="1"
                max="10"
                value={report.score}
                onChange={(event) =>
                  update(index, {
                    score: Number(event.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                type="number"
                min="0"
                value={report.distanceMeters}
                onChange={(event) =>
                  update(index, {
                    distanceMeters: event.target.value === '' ? '' : Number(event.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                type="number"
                min="0"
                value={report.accuracyMeters}
                onChange={(event) =>
                  update(index, {
                    accuracyMeters: event.target.value === '' ? '' : Number(event.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                type="number"
                min="0"
                value={report.ageMinutes}
                onChange={(event) =>
                  update(index, {
                    ageMinutes: Number(event.target.value),
                  })
                }
                fullWidth
              />
              <Stack
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  minHeight: '34px',
                  '& )': {
                    minWidth: '180px',
                    flex: '1',
                  },
                  padding: '9px 10px',
                  border: '1px solid #e7ebf0',
                  borderRadius: '9px',
                  background: '#fbfcfd',
                  '& small': {
                    color: 'var(--muted)',
                    direction: 'ltr',
                    textAlign: 'right',
                  },
                  color: 'var(--text)',
                  width: 'auto',
                  justifyContent: 'center',
                }}
              >
                <Checkbox
                  checked={report.locationVerified}
                  onChange={(event) =>
                    update(index, {
                      locationVerified: event.target.checked,
                    })
                  }
                />
              </Stack>
              <Typography
                sx={{
                  direction: 'ltr',
                  textAlign: 'right',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
              >
                {result.source} × {result.location} × {result.accuracy} × {result.freshness}
              </Typography>
              <Typography>{result.total.toFixed(3)}</Typography>
              <Button
                type="button"
                onClick={() =>
                  setReports((current) => current.filter((_, itemIndex) => itemIndex !== index))
                }
                sx={{
                  color: '#b42318',
                  borderColor: '#f1b9b4',
                  background: '#fff5f4',
                }}
              >
                הסרה
              </Button>
            </Stack>
          );
        })}
      </Stack>
      <Button
        type="button"
        onClick={() =>
          setReports((current) => [
            ...current,
            {
              id: Date.now(),
              source: 'user',
              score: 5,
              distanceMeters: '',
              accuracyMeters: '',
              ageMinutes: 0,
              locationVerified: false,
            },
          ])
        }
        sx={{
          background: '#edf2f7',
          color: 'var(--text)',
          '&:hover': {
            background: '#ddebff',
            color: '#1269e8',
          },
        }}
      >
        הוספת דיווח לסימולציה
      </Button>
    </Stack>
  );
};
const BucketEditor = ({ title, items, maxKey, maxLabel, onChange, onAdd, onRemove }) => (
  <Stack
    sx={{
      display: 'grid',
      gap: '10px',
      padding: '14px',
      border: '1px solid var(--line)',
      borderRadius: '12px',
    }}
  >
    <Typography>{title}</Typography>
    <Stack
      sx={{
        display: 'grid',
        gap: '8px',
      }}
    >
      {items.map((item, index) => (
        <Stack
          key={`${maxKey}-${index}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 70px',
            gap: '7px',
            alignItems: 'end',
          }}
        >
          <Stack>
            {maxLabel}
            <TextField
              type="number"
              min="0"
              value={item[maxKey]}
              onChange={(event) => onChange(index, maxKey, Number(event.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            משקל
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={item.weight}
              onChange={(event) => onChange(index, 'weight', Number(event.target.value))}
              fullWidth
            />
          </Stack>
          <Button
            type="button"
            onClick={() => onRemove(index)}
            sx={{
              color: '#b42318',
              borderColor: '#f1b9b4',
              background: '#fff5f4',
            }}
          >
            הסרה
          </Button>
        </Stack>
      ))}
    </Stack>
    <Button
      type="button"
      onClick={onAdd}
      sx={{
        background: '#edf2f7',
        color: 'var(--text)',
        '&:hover': {
          background: '#ddebff',
          color: '#1269e8',
        },
      }}
    >
      הוספת מדרגה
    </Button>
  </Stack>
);
const LoadAlgorithmSettingsPage = ({ settings, onSave, loading }) => {
  const [draft, setDraft] = useState(settings);
  useEffect(() => setDraft(settings), [settings]);
  if (!draft)
    return (
      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '22px',
          direction: 'rtl',
          maxWidth: '1680px',
          margin: '0 auto',
          [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            gap: '14px',
          },
        })}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 'bold',
          }}
        >
          אלגוריתם עומס
        </Typography>
      </Stack>
    );
  const updateSection = (section, key, value) =>
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: Number(value),
      },
    }));
  const updateBucket = (section, index, key, value) =>
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        buckets: current[section].buckets.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                [key]: value,
              }
            : item,
        ),
      },
    }));
  const removeBucket = (section, index) =>
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        buckets: current[section].buckets.filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  const addBucket = (section, maxKey) =>
    setDraft((current) => {
      const buckets = current[section].buckets;
      const previousMax = Number(buckets.at(-1)?.[maxKey] || 0);
      return {
        ...current,
        [section]: {
          ...current[section],
          buckets: [
            ...buckets,
            {
              [maxKey]: previousMax + 10,
              weight: 0.1,
            },
          ],
        },
      };
    });
  return (
    <Stack
      dir="rtl"
      sx={(theme) => ({
        display: 'grid',
        gap: '20px',
        direction: 'rtl',
        maxWidth: '1680px',
        margin: '0 auto',
        [theme.breakpoints.down('sm')]: {
          maxWidth: '100%',
          gap: '14px',
        },
        paddingBottom: '40px',
      })}
    >
      <Stack
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '4px',
          direction: 'rtl',
          [theme.breakpoints.down('lg')]: {
            display: 'grid',
          },
          flexDirection: 'row',
          [theme.breakpoints.down('sm')]: {
            minWidth: '0',
            gap: '12px',
            position: 'relative',
            paddingLeft: '0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
          },
        })}
      >
        <Stack>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 'bold',
            }}
          >
            אלגוריתם עומס
          </Typography>
          <Typography>גרסה {draft.version || 1} · דיווחים קיימים אינם משתנים</Typography>
        </Stack>
        <Button disabled={loading} onClick={() => onSave(draft)}>
          שמירת הגדרות
        </Button>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>מקורות וזמני תוקף</Typography>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))',
            gap: '12px',
            '& article': {
              display: 'grid',
              gap: '10px',
              padding: '14px',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              background: '#f8fafc',
            },
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          {Object.keys(sourceLabels).map((source) => (
            <Card key={source}>
              <Typography>{sourceLabels[source]}</Typography>
              <Stack>
                משקל
                <TextField
                  type="number"
                  min="0"
                  max="2"
                  step="0.05"
                  value={draft.sourceWeights[source]}
                  onChange={(event) => updateSection('sourceWeights', source, event.target.value)}
                  fullWidth
                />
              </Stack>
              {source !== 'unknown' && (
                <Stack>
                  תוקף בדקות
                  <TextField
                    type="number"
                    min="1"
                    value={draft.validityMinutes[source]}
                    onChange={(event) =>
                      updateSection('validityMinutes', source, event.target.value)
                    }
                    fullWidth
                  />
                </Stack>
              )}
            </Card>
          ))}
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>משקלי איכות הדיווח</Typography>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(170px, 1fr))',
            gap: '12px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <Stack>
            מיקום מאומת
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={draft.locationWeight.verified}
              onChange={(event) => updateSection('locationWeight', 'verified', event.target.value)}
              fullWidth
            />
          </Stack>
          <Stack>
            ללא מרחק
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={draft.locationWeight.missing}
              onChange={(event) => updateSection('locationWeight', 'missing', event.target.value)}
              fullWidth
            />
          </Stack>
          <Stack>
            מרחק מעל כל המדרגות
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={draft.locationWeight.fallback}
              onChange={(event) => updateSection('locationWeight', 'fallback', event.target.value)}
              fullWidth
            />
          </Stack>
          <Stack>
            ללא דיוק
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={draft.accuracyWeight.missing}
              onChange={(event) => updateSection('accuracyWeight', 'missing', event.target.value)}
              fullWidth
            />
          </Stack>
          <Stack>
            דיוק מעל כל המדרגות
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={draft.accuracyWeight.fallback}
              onChange={(event) => updateSection('accuracyWeight', 'fallback', event.target.value)}
              fullWidth
            />
          </Stack>
          <Stack>
            גיל מעל כל המדרגות
            <TextField
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={draft.freshnessWeight.fallback}
              onChange={(event) => updateSection('freshnessWeight', 'fallback', event.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(260px, 1fr))',
            gap: '14px',
            alignItems: 'start',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <BucketEditor
            title="מרחק מהמקום"
            items={draft.locationWeight.buckets}
            maxKey="maxMeters"
            maxLabel="עד מטרים"
            onChange={(...args) => updateBucket('locationWeight', ...args)}
            onAdd={() => addBucket('locationWeight', 'maxMeters')}
            onRemove={(index) => removeBucket('locationWeight', index)}
          />
          <BucketEditor
            title="דיוק GPS"
            items={draft.accuracyWeight.buckets}
            maxKey="maxMeters"
            maxLabel="עד מטרים"
            onChange={(...args) => updateBucket('accuracyWeight', ...args)}
            onAdd={() => addBucket('accuracyWeight', 'maxMeters')}
            onRemove={(index) => removeBucket('accuracyWeight', index)}
          />
          <BucketEditor
            title="טריות הדיווח"
            items={draft.freshnessWeight.buckets}
            maxKey="maxMinutes"
            maxLabel="עד דקות"
            onChange={(...args) => updateBucket('freshnessWeight', ...args)}
            onAdd={() => addBucket('freshnessWeight', 'maxMinutes')}
            onRemove={(index) => removeBucket('freshnessWeight', index)}
          />
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Stack>
          <Typography>ביטחון בדיווחים אמיתיים והשפעת AI</Typography>
          <Typography
            sx={(theme) => ({
              color: 'var(--muted)',
              [theme.breakpoints.down('lg')]: {
                margin: '4px 0 0',
                fontSize: '12px',
                display: 'none',
              },
              margin: '6px 0 0',
              fontSize: '11px',
            })}
          >
            הביטחון מחושב מדיווחי משתמש ו־GPS, ובהתאם אליו נקבעת השפעת ה־AI על התוצאה.
          </Typography>
        </Stack>
        <Stack
          sx={{
            display: 'grid',
            gap: '14px',
          }}
        >
          <Stack
            sx={{
              display: 'grid',
              gap: '14px',
              padding: '16px',
              border: '1px solid var(--line)',
              borderRadius: '14px',
              background: '#f8fafc',
              '& header': {
                display: 'grid',
                gap: '4px',
              },
              '& header p': {
                margin: '0',
              },
            }}
          >
            <Stack>
              <Typography>חישוב ביטחון מדיווחים אמיתיים</Typography>
              <Typography
                sx={(theme) => ({
                  color: 'var(--muted)',
                  [theme.breakpoints.down('lg')]: {
                    margin: '4px 0 0',
                    fontSize: '12px',
                    display: 'none',
                  },
                  margin: '2px 0 0',
                  fontSize: '11px',
                })}
              >
                משתמשים, GPS ורמת ההסכמה בין הדיווחים.
              </Typography>
            </Stack>
            <Stack
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(170px, 1fr))',
                gap: '12px',
                [theme.breakpoints.down('lg')]: {
                  gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
                },
                [theme.breakpoints.down('sm')]: {
                  gridTemplateColumns: '1fr',
                },
              })}
            >
              {realConfidenceFields.map((key) => (
                <Stack key={key}>
                  {fieldLabels[key] || key}
                  <TextField
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.confidence[key]}
                    onChange={(event) => updateSection('confidence', key, event.target.value)}
                    fullWidth
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Stack
            sx={{
              display: 'grid',
              gap: '14px',
              padding: '16px',
              border: '1px solid var(--line)',
              borderRadius: '14px',
              background: '#f5f8ff',
              '& header': {
                display: 'grid',
                gap: '4px',
              },
              '& header p': {
                margin: '0',
              },
              borderColor: '#c7d7fe',
            }}
          >
            <Stack>
              <Typography>השפעת AI</Typography>
              <Typography
                sx={(theme) => ({
                  color: 'var(--muted)',
                  [theme.breakpoints.down('lg')]: {
                    margin: '4px 0 0',
                    fontSize: '12px',
                    display: 'none',
                  },
                  margin: '2px 0 0',
                  fontSize: '11px',
                })}
              >
                ככל שהביטחון בדיווחים האמיתיים עולה, השפעת ה־AI פוחתת.
              </Typography>
            </Stack>
            <Stack
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(170px, 1fr))',
                gap: '12px',
                [theme.breakpoints.down('lg')]: {
                  gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
                },
                [theme.breakpoints.down('sm')]: {
                  gridTemplateColumns: '1fr',
                },
              })}
            >
              {aiConfidenceFields.map((key) => (
                <Stack key={key}>
                  {fieldLabels[key] || key}
                  <TextField
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.confidence[key]}
                    onChange={(event) => updateSection('confidence', key, event.target.value)}
                    fullWidth
                  />
                </Stack>
              ))}
              <Stack>
                סף ביטחון אמיתי
                <TextField
                  type="number"
                  min="0"
                  max="100"
                  value={draft.aiBlend.minimumRealConfidence}
                  onChange={(event) =>
                    updateSection('aiBlend', 'minimumRealConfidence', event.target.value)
                  }
                  fullWidth
                />
              </Stack>
              <Stack>
                השפעת AI מרבית
                <TextField
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={draft.aiBlend.maximumInfluence}
                  onChange={(event) =>
                    updateSection('aiBlend', 'maximumInfluence', event.target.value)
                  }
                  fullWidth
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>מיפוי ציון למצב</Typography>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(170px, 1fr))',
            gap: '12px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          {draft.statuses.map((status, index) => (
            <Stack key={status.key}>
              {status.label} — ציון מרבי
              <TextField
                type="number"
                min="1"
                max="10"
                disabled={index === draft.statuses.length - 1}
                value={status.maxScore}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    statuses: current.statuses.map((item, itemIndex) =>
                      itemIndex === index
                        ? {
                            ...item,
                            maxScore: Number(event.target.value),
                          }
                        : item,
                    ),
                  }))
                }
                fullWidth
              />
            </Stack>
          ))}
        </Stack>
      </Stack>

      <AlgorithmSimulator settings={draft} />
    </Stack>
  );
};
export default LoadAlgorithmSettingsPage;
